/** @jsxImportSource @emotion/react */
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import useNotifications from '../../../../shared/hooks/use-notifications';
import { getUserChannelData, shouldHideDTDetails } from '../../../login/v1/utility';
import useAddonPurchase from '../../../../shared/hooks/use-addon-purchase';
import { useCallingAddon } from '../hooks/use-calling-addon';
import { useCallback } from 'react';
import useQuery from '../../../../shared/hooks/useQuery';
import { trialDetailsAtom } from './call-free-trial/states';
import { getTrialDetails } from './call-free-trial/requests';
import { getCurrencySymbol } from '../../../../shared/utils/currency-symbol';
import { openPopup } from '../../../../shared/utils/popup';
import QSModal from '../../../../shared/components/molecules/modal';
import { css } from '@emotion/react';
import { QSButton, QSIconButton, QSTypography } from '../../../../shared/components/atoms';
import ProgressiveImage from '../../../../shared/components/atoms/progressive-image';
import { PlanAddonTypes } from '../../../renew-plan/v1/typings';
import { Close } from '@mui/icons-material';
import Loader from '../../../../shared/components/molecules/loader';
import WaVoiceImage from '../../images/wa-voice-image.png';
import WaVoiceImageShort from '../../images/wa-voice-image-short.png';
import { waVoiceTrialAutoOpenAtom, waVoiceUpgradeModalOpen } from '../states';
import PhoneOutlined from '../../../../shared/icons/PhoneOutlined';
import { MAIN_IN_SUPPORT_NUMBER } from '../../../../shared/constants';
import { FreeTrialStep, formatBillingPeriod } from './call-free-trial/typings';
import { freeTrialStepAtom } from './call-free-trial/states';
import { autoOpenAnimationCss, FEATURES, WA_VOICE_TRIAL_DISMISSED } from '../constants';
import { selectedOrganizationAtom } from '../../../../shared/states/login';
import { useSelectedOrganization } from '../../../../shared/hooks/use-organizations';
import { RUPEE } from '../../../../shared/constants/currency-symbol';

export default function WaVoiceUpgradeModal() {
  const [upgradeModal, setUpgradeModal] = useAtom(waVoiceUpgradeModalOpen);
  const [isAutoOpen, setIsAutoOpen] = useAtom(waVoiceTrialAutoOpenAtom);
  const { addNotification } = useNotifications();
  const channelData = getUserChannelData();
  const hideDTDetails = shouldHideDTDetails();
  const setFreeTrialStep = useSetAtom(freeTrialStepAtom);
  const currentOrg = useSelectedOrganization();
  const onClose = useCallback(
    (event?: any, reason?: string) => {
      if (reason === 'backdropClick') return;
      const dismissedKey = currentOrg ? `${WA_VOICE_TRIAL_DISMISSED}_${currentOrg.orgId}` : WA_VOICE_TRIAL_DISMISSED;
      localStorage.setItem(dismissedKey, 'true');
      setIsAutoOpen(false);
      setUpgradeModal(false);
    },
    [setUpgradeModal, setIsAutoOpen, currentOrg]
  );
  const { purchaseAddon, loading: purchaseLoading } = useAddonPurchase(PlanAddonTypes.CallingLicense, {
    onSuccess: onClose,
  });
  const { canShowCallingFreeTrial: showTrialPlan } = useCallingAddon();

  const queryFn = () => getTrialDetails(PlanAddonTypes.CallingLicense);
  const { requestData: trialDetails } = useQuery({
    requestAtom: trialDetailsAtom,
    queryFunction: queryFn,
    autoFetch: showTrialPlan,
    cacheData: true,
  });
  const trialDetailsData = trialDetails.data;
  const currencySymbol = trialDetailsData?.currencyCode ? getCurrencySymbol(trialDetailsData.currencyCode) : RUPEE;
  const priceDisplay = trialDetailsData?.amountPerUnit ?? 500;
  const billingPeriodLabel = trialDetailsData
    ? formatBillingPeriod(trialDetailsData.period, trialDetailsData.periodUnit)
    : 'per license per month';

  let contactUsLink = `https://wa.me/${MAIN_IN_SUPPORT_NUMBER}?text=I%27m%20interested%20in%20WhatsApp%20Calls`;
  if (hideDTDetails) {
    contactUsLink = `https://wa.me/${channelData?.supportNumber}?text=I%27m%20interested%20in%20WhatsApp%20Calls`;
  }

  const onClickContactUs = useCallback(() => {
    try {
      if (hideDTDetails) {
        openPopup(contactUsLink, '_blank');
        onClose();
      } else if (showTrialPlan) {
        onClose();
        setFreeTrialStep(FreeTrialStep.TrialDetails);
      } else {
        purchaseAddon();
      }
    } catch (error) {
      addNotification({
        message: (error as Error).message ?? 'An error occurred. Please try again',
        type: 'error',
      });
    }
  }, [addNotification, onClose, hideDTDetails, contactUsLink, setFreeTrialStep, showTrialPlan, purchaseAddon]);

  if (!upgradeModal) {
    return null;
  }

  const channelName = channelData?.name || 'your workspace';

  const renderContent = () => {
    if (!showTrialPlan) {
      return (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            border-radius: 8px;
            overflow: hidden;
            background: white;
            outline: none;
            position: relative;
            padding: 16px;
            max-width: 730px;
          `}
        >
          <QSIconButton
            css={css`
              position: absolute;
              top: 8px;
              right: 8px;
            `}
            sx={{}}
            onClick={onClose}
          >
            <Close />
          </QSIconButton>
          <div
            css={css`
              flex: 1;
              overflow: auto;
              display: flex;
              align-items: flex-start;
              flex-wrap: wrap;
            `}
          >
            <div
              css={css`
                display: flex;
                justify-content: space-between;
                align-items: center;
                flex: 1;
                background-color: #f6f1e7;
                border-radius: 8px;
                overflow: hidden;
                width: 320px;
              `}
            >
              <ProgressiveImage
                imgProps={{ width: '100%', height: '374px', style: { objectFit: 'cover' } }}
                src={WaVoiceImageShort}
              />
            </div>
            <div
              css={css`
                flex: 1;
                padding: 0;
                padding-left: 24px;
                max-width: 356px;
              `}
            >
              <span
                css={css`
                  border-radius: 4px;
                  padding: 6px 8px;
                  display: inline-flex;
                  align-items: center;
                  font-size: 12px;
                  margin-top: 0;
                  font-weight: 600;
                  color: #596bd7;
                  background-color: #596bd714;
                `}
              >
                <PhoneOutlined width={16} height={16} color="#596bd7" />
                <span
                  css={css`
                    margin-left: 5px;
                  `}
                >
                  WhatsApp Calls
                </span>
              </span>
              <QSTypography
                css={css`
                  font-weight: 600;
                  padding-right: 12px;
                  font-size: 20px;
                  overflow: hidden;
                  line-height: 28px;
                  margin-bottom: 16px;
                  margin-top: 8px;
                `}
              >
                Seamless WhatsApp Calls on {hideDTDetails ? channelName : 'DoubleTick'} with Recordings and Call history
              </QSTypography>

              {FEATURES.map((feature, key) => {
                const Icon = feature?.icon;
                return (
                  <div
                    key={key}
                    css={css`
                      display: inline-flex;
                      align-items: flex-start;
                      justify-content: flex-start;
                      margin-bottom: 10px;
                      font-weight: 500;
                      gap: 12px;
                    `}
                  >
                    <div
                      style={{
                        height: '32px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: feature.iconColor ?? '#2d2d2d',
                      }}
                    >
                      <Icon width={24} height={24} />
                    </div>
                    <span
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                      `}
                    >
                      <div
                        css={css`
                          font-family: Inter;
                          font-size: 14px;
                          font-weight: 500;
                          line-height: 16.94px;
                          text-align: left;
                          color: #2d2d2d;
                        `}
                      >
                        {feature?.featureLabel}
                      </div>
                      <div
                        css={css`
                          font-family: Inter;
                          font-size: 12px;
                          font-weight: 500;
                          line-height: 14.52px;
                          color: #7a7a7a;
                        `}
                      >
                        {feature?.desc}
                      </div>
                    </span>
                  </div>
                );
              })}
              <div
                css={css`
                  margin-top: 8px;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  gap: 10px;
                `}
              >
                <QSButton
                  css={css`
                    height: 40px;
                    width: 100%;
                    white-space: nowrap;
                  `}
                  onClick={onClickContactUs}
                >
                  {hideDTDetails ? (
                    'Contact Us'
                  ) : (
                    <>
                      {purchaseLoading ? (
                        <Loader
                          size={16}
                          loaderProps={{
                            sx: {
                              color: 'white',
                            },
                          }}
                        />
                      ) : (
                        'Purchase Now'
                      )}
                    </>
                  )}
                </QSButton>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div
        css={[
          css`
            display: flex;
            flex-direction: column;
            border-radius: 8px;
            overflow: hidden;
            background: white;
            outline: none;
            position: relative;
            padding: 16px;
            max-width: 730px;
          `,
          isAutoOpen && autoOpenAnimationCss,
        ]}
      >
        <QSIconButton
          css={css`
            position: absolute;
            top: 8px;
            right: 8px;
          `}
          sx={{}}
          onClick={onClose}
        >
          <Close />
        </QSIconButton>
        <div
          css={css`
            flex: 1;
            overflow: auto;
            display: flex;
            align-items: flex-start;
            flex-wrap: wrap;
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex: 1;
              background-color: #f6f1e7;
              border-radius: 8px;
              overflow: hidden;
              width: 320px;
            `}
          >
            <ProgressiveImage
              imgProps={{
                width: '100%',
                height: hideDTDetails ? '390px' : '488px',
                style: {
                  objectFit: 'cover',
                },
              }}
              src={WaVoiceImage}
            />
          </div>
          <div
            css={css`
              flex: 1;
              padding: 0;
              padding-left: 24px;
              max-width: 356px;
            `}
          >
            <span
              css={css`
                border-radius: 4px;
                padding: 6px 8px;
                display: inline-flex;
                align-items: center;
                font-size: 12px;
                margin-top: 0;
                margin-bottom: 12px;
                font-weight: 600;
                color: #596bd7;
                background-color: #596bd714;
              `}
            >
              <PhoneOutlined width={16} height={16} color="#596bd7" />
              <span
                css={css`
                  margin-left: 5px;
                `}
              >
                WhatsApp Calls
              </span>
            </span>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 4px;
                margin-bottom: 16px;
              `}
            >
              <QSTypography
                css={css`
                  color: #2d2d2d;
                  font-family: Inter;
                  font-size: 20px;
                  font-style: normal;
                  font-weight: 600;
                  line-height: 140%;
                `}
              >
                Try WhatsApp calling for free on {hideDTDetails ? channelName : 'DoubleTick'}
              </QSTypography>
              <QSTypography
                css={css`
                  color: var(--Neutral-Secondary, #7a7a7a);
                  font-family: Inter;
                  font-size: 12px;
                  font-style: normal;
                  font-weight: 500;
                  line-height: 140%;
                `}
              >
                WhatsApp calls directly on {hideDTDetails ? channelName : 'DoubleTick'} with free of cost incoming
                calls. Get 30-Day free trial.
              </QSTypography>
            </div>
            {FEATURES.map((feature, key) => {
              const Icon = feature?.icon;
              return (
                <div
                  key={key}
                  css={css`
                    display: inline-flex;
                    align-items: flex-start;
                    justify-content: flex-start;
                    margin-bottom: 12px;
                    font-weight: 500;
                    gap: 12px;
                  `}
                >
                  <div
                    style={{
                      height: '32px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: feature.iconColor ?? '#2d2d2d',
                    }}
                  >
                    <Icon width={24} height={24} />
                  </div>
                  <span
                    css={css`
                      display: flex;
                      flex-direction: column;
                      gap: 4px;
                    `}
                  >
                    <div
                      css={css`
                        font-family: Inter;
                        font-size: 14px;
                        font-weight: 500;
                        line-height: 16.94px;
                        text-align: left;
                        color: #2d2d2d;
                      `}
                    >
                      {feature?.featureLabel}
                    </div>
                    <div
                      css={css`
                        font-family: Inter;
                        font-size: 12px;
                        font-weight: 500;
                        line-height: 14.52px;
                        color: #7a7a7a;
                      `}
                    >
                      {feature?.desc}
                    </div>
                  </span>
                </div>
              );
            })}
            {!hideDTDetails && (
              <div
                css={css`
                  margin-top: 6px;
                  background-color: rgba(60, 132, 97, 0.1);
                  border-radius: 8px;
                  padding: 12px 16px;
                  display: flex;
                  flex-direction: column;
                  gap: 2px;
                `}
              >
                <QSTypography
                  css={css`
                    color: #3c8461;
                    font-family: Inter;
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 140%;
                  `}
                >
                  Start your trial with 5 calling licenses
                </QSTypography>
                <QSTypography
                  css={css`
                    color: #7a7a7a;
                    font-family: Inter;
                    font-size: 12px;
                    font-weight: 500;
                    line-height: 140%;
                  `}
                >
                  One license per team member
                </QSTypography>
              </div>
            )}
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
              `}
            >
              <QSButton
                css={css`
                  height: 40px;
                  width: 100%;
                  white-space: nowrap;
                  font-size: 14px;
                  font-weight: 600;
                  margin-block: 10px;
                `}
                onClick={onClickContactUs}
              >
                {hideDTDetails ? 'Contact Us' : 'Start free trial'}
              </QSButton>
              {!hideDTDetails && (
                <QSTypography
                  css={css`
                    color: #7a7a7a;
                    font-family: Inter;
                    font-size: 10px;
                    font-weight: 400;
                    line-height: 140%;
                    text-align: center;
                  `}
                >
                  No payment today. {currencySymbol}
                  {priceDisplay} {billingPeriodLabel} after trial ends
                </QSTypography>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };
  return (
    <QSModal
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
      `}
      open={upgradeModal}
      onClose={onClose}
    >
      {renderContent()}
    </QSModal>
  );
}
